import React, { useEffect, useState } from 'react'
import { Container } from 'react-bootstrap'
import {
  NavLink,
  Route,
  Switch,
  useParams,
  useHistory,
  useLocation,
} from 'react-router-dom'
import {
  useErrorService,
  ABSpinner,
  useNotificationService,
  useUserService,
  paymentType as paymentTypeConst,
  Button,
  NavTabs,
} from '@abroad/components'
import IndividualSOWForm from './IndividualSOWForm'
import { PageHeader } from '../common'
import Labels from '../../constants/labels'
import API from '../../utils/API'
import {
  isPlatformSchedulingAvailable,
  buildCoachSchedulingFlagsMap,
  resolveCoachSchedulingFlags,
} from '../scheduling/schedulingPreconditions'
import { getCoachingPlanObject, isNumber } from '../../utils/utility'
import TerminatedModal from './TerminatedModal'
import { sowStatus } from '@abroad/components/src/helper/common'
import CoachingPlanSessions from './CoachingPlanSessions'
import CoachingPlanPayment from './CoachingPlanPayment'

const getCoachId = (coach) => coach?._id ?? coach?.id ?? null

const coachToSelectOption = (coach) => {
  const id = getCoachId(coach)
  if (!id) return null
  return {
    label: coach?.fullName,
    value: id,
    hasCoachBio: coach?.hasCoachBio,
    hasQuestBio: coach?.hasQuestBio,
  }
}

export const formInitialValues = {
  sowStatus: 'DRAFT',
  // SOW Details
  isPaymentRequired: 'true',
  sowCoachId: null,
  planName: '',
  sowCopy: [],
  sowType: 'REGULAR',
  // Billing
  paymentCycle: paymentTypeConst.ONETIME,
  invoiceEvery: '',
  numberOfBillingCycle: '',
  subscriptionAmount: '',
  // Coaching
  sessionPerCoachingCycle: '',
  sessionDuration: '',
  report360: false,
  reportPlan360: '',
  impactReport: false,
  impactReportPlan: '',
  coachingRate: '',
  clientTimeZone: '',
  onetimeAmount: '',
  upfrontFee: '',
  totalSession: '',
  sugTotalSession: '',
  planStartDate: null,
  typeOfCoachingCycle: 'Monthly',
  coachingRateType: 'hourly',
  growthCoachingRateType: 'hourly',
  numberOfCoachingCycle: '',
  // Conditional Scheduling
  isConditionalSchedulingEnabled: false,
  schedulingConditions: [],
  // Scheduling Type
  isPlatformScheduling: true,
  // Extra
  isReadyForPayment: '',
  hasEditedTotalSession: false,
  setToBeTerminated: '',
  includeGrowthPlan: false,
  growthSessionDuration: '',
}

const CoachingPlanDetails = () => {
  const [initialValues, setInitialValues] = useState(formInitialValues)
  const [isLoading, setIsLoading] = useState(true)
  const [clientName, setClientName] = useState('')
  const [isUpdatePlanLoading, setIsUpdatePlanLoading] = useState(false)
  const Error = useErrorService()
  const history = useHistory()
  const location = useLocation()
  const Notification = useNotificationService()
  let { clientId, planId, coachId } = useParams()
  const { user } = useUserService()
  const [showModal, setShowModal] = useState(false)
  const [coaches, setCoaches] = useState([])
  const [isPlanTerminated, setIsPlanTerminated] = useState(false)
  const [showTerminatedModal, setTerminatedModal] = useState(false)
  // Add state to store current coachId
  const [currentCoachId, setCurrentCoachId] = useState(coachId)

  const handleOpenModal = () => {
    setTerminatedModal(true)
  }
  const handleCloseModal = () => {
    setTerminatedModal(false)
  }
  const pages = [
    {
      path: `/coach/clients/${clientId}/plans/${planId}/sow`,
      name: 'SOW',
      defaultUrl: `/coach/clients/${clientId}/plans/${planId}`,
    },
    {
      path: `/coach/clients/${clientId}/plans/${planId}/sessions`,
      name: 'Session',
    },
    ...(user?.isAdmin
      ? [
          {
            path: `/coach/clients/${clientId}/plans/${planId}/payment`,
            name: 'Payment',
          },
        ]
      : []),
  ]

  useEffect(() => {
    const getPlanDetails = async () => {
      try {
        const { data } = await API.AdminAPI.coachPlan.getCoachingPlan(
          clientId,
          planId,
        )
        if (data && data?.sow === null) {
          history.push(`/coach/clients/${clientId}`)
        }
        if (data) {
          const assignedCoachId = getCoachId(data?.sow?.coachId)
          const currentCoachDetails = coachToSelectOption(data?.sow?.coachId)
          setClientName(data?.clientName)
          if (currentCoachDetails) {
            setCoaches([currentCoachDetails])
          }
          setCurrentCoachId(assignedCoachId)

          if (data?.sow?.sowStatus === Labels.sowStatusLabel[2].value) {
            setIsPlanTerminated(true)
          }
          const values = getCoachingPlanObject(data)
          setInitialValues((prev) => ({
            ...prev,
            sowCoachId: assignedCoachId,
            paymentUpToDate: data?.sow?.hasPaid ? 'yes' : 'no',
            clientTimeZone: data?.sow?.timeZone ? data?.sow?.timeZone : '',
            isPaymentRequired:
              data?.sow?.isPaymentRequired === true ? 'true' : 'false',
            ...values,
          }))
        }
        setIsLoading(false)
      } catch (e) {
        setIsLoading(false)
        Error.showError(e)
      }
    }

    const fetchClientProfile = async () => {
      try {
        const [{ data }, { data: allCoaches = [] }] = await Promise.all([
          API.AdminAPI.coachClient.getClientSpecificData(clientId, {
            fields: ['fullName', 'coachIds'],
          }),
          API.AdminAPI.userList.getCoaches(),
        ])
        if (data) {
          // Use currentCoachId if available, otherwise use coachId from params
          const activeCoachId = currentCoachId || coachId
          const coachFlagsMap = buildCoachSchedulingFlagsMap(allCoaches)
          const coachSchedulingFlagsFromState =
            location?.state?.coachSchedulingFlags || {}
          const sowCoach = data?.coachIds?.find(
            (coach) => getCoachId(coach) === activeCoachId,
          )
          const filterIndividualCoach = data?.coachIds?.filter(
            (coach) => coach?.isIndiCoach,
          )
          const getCoachSchedulingFlags = (coachIdKey, coachFromClient) =>
            resolveCoachSchedulingFlags(coachIdKey, {
              coachFromClient,
              coachFlagsMap,
              coachSchedulingFlagsFromState,
              activeCoachId,
            })
          const selectedCoachId = getCoachId(sowCoach) || activeCoachId
          const selectedCoachFlags = getCoachSchedulingFlags(
            selectedCoachId,
            sowCoach,
          )

          setCoaches(
            filterIndividualCoach
              ?.map((coach) => {
                const schedulingFlags = getCoachSchedulingFlags(
                  getCoachId(coach),
                  coach,
                )

                return {
                  label: coach?.fullName,
                  value: getCoachId(coach),
                  ...schedulingFlags,
                }
              })
              .filter((coach) => coach.value),
          )

          setClientName(data?.fullName)

          const coachSchedulingFlagsByCoachId = filterIndividualCoach?.reduce(
            (acc, coach) => {
              const id = getCoachId(coach)
              if (id) {
                acc[id] = getCoachSchedulingFlags(id, coach)
              }
              return acc
            },
            {},
          )

          setInitialValues((prev) => ({
            ...prev,
            sowCoachId: selectedCoachId,
            coachSchedulingFlagsByCoachId,
            isPlatformScheduling:
              isPlatformSchedulingAvailable(selectedCoachFlags),
          }))

          setIsLoading(false)
        }
      } catch (e) {
        Error.showError(e)
        setIsLoading(false)
      }
    }

    if (planId !== 'create') {
      getPlanDetails()
    } else {
      if (!user?.isAdmin) {
        history.push(`/coach/clients/${clientId}`)
      }
      fetchClientProfile()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, planId, coachId])

  // Simple function to refresh SOW data
  const refreshSOWData = async () => {
    if (planId === 'create') return

    try {
      const { data } = await API.AdminAPI.coachPlan.getCoachingPlan(
        clientId,
        planId,
      )
      if (data && data?.sow) {
        const assignedCoachId = getCoachId(data?.sow?.coachId)
        const values = getCoachingPlanObject(data)
        setInitialValues((prev) => ({
          ...prev,
          sowCoachId: assignedCoachId,
          paymentUpToDate: data?.sow?.hasPaid ? 'yes' : 'no',
          clientTimeZone: data?.sow?.timeZone ? data?.sow?.timeZone : '',
          isPaymentRequired:
            data?.sow?.isPaymentRequired === true ? 'true' : 'false',
          ...values,
        }))

        if (assignedCoachId !== currentCoachId) {
          setCurrentCoachId(assignedCoachId)
          const coachOption = coachToSelectOption(data?.sow?.coachId)
          if (coachOption) {
            setCoaches([coachOption])
          }
        }

        if (data?.sow?.sowStatus === Labels.sowStatusLabel[2].value) {
          setIsPlanTerminated(true)
        } else {
          setIsPlanTerminated(false)
        }
      }
    } catch (e) {
      Error.showError(e)
    }
  }

  // Call refreshSOWData when SOW tab is accessed
  useEffect(() => {
    if (
      location?.pathname.includes('/sow') ||
      location?.pathname === `/coach/clients/${clientId}/plans/${planId}`
    ) {
      refreshSOWData()
    }
  }, [location?.pathname])

  if (isLoading) {
    return <ABSpinner />
  }

  const onSubmit = async (values = null) => {
    try {
      setIsUpdatePlanLoading(true)
      let params = {
        isPaymentRequired: values.isPaymentRequired === 'true' ? true : false,
        // SOW Details
        coachId: values.sowCoachId || currentCoachId, // Ensure coachId is always present
        planName: values.planName,
        sowType: values.sowType || 'REGULAR',
        ...(values.sowType === 'QUEST' &&
          values.questId && { questId: values.questId }),
        // Billing
        paymentCycle: values.paymentCycle,

        ...(values.paymentCycle !== paymentTypeConst.RECURRING && {
          onetimeAmount: values.onetimeAmount,
        }),

        ...((values.paymentCycle === paymentTypeConst.ONETIME ||
          (values?.invoiceEvery && values?.numberOfBillingCycle)) && {
          totalDurationOfContract: values.totalDurationOfContract,
        }),

        ...(values.paymentCycle === paymentTypeConst.RECURRING && {
          invoiceEvery: isNumber(values.invoiceEvery)
            ? Number(values.invoiceEvery)
            : '',
          numberOfBillingCycle: values.numberOfBillingCycle,
          subscriptionAmount: values.subscriptionAmount,
          upfrontFee: values.upfrontFee,
        }),
        typeOfCoachingCycle: values.typeOfCoachingCycle,

        billingIndefinite:
          values.paymentCycle !== paymentTypeConst.ONETIME
            ? values.numberOfBillingCycle
              ? false
              : true
            : values.totalDurationOfContract
            ? false
            : true,

        // Coaching
        sessionPerCoachingCycle: values.sessionPerCoachingCycle,
        numberOfCoachingCycle: Number(values.numberOfCoachingCycle),
        sessionDuration: values.sessionDuration,
        includeGrowthSession: values.includeGrowthSession,
        ...(values.includeGrowthSession && {
          growthSessionDuration: values.growthSessionDuration,
          growthCoachingRateType: values.growthCoachingRateType,
          growthCoachingRate: values.growthCoachingRate,
        }),
        // report360: values.report360 ? values.reportPlan360 : null,
        // impactReport: values.impactReport ? values.impactReportPlan : null,

        coachingRate: values.coachingRate,
        coachingRateType: values.coachingRateType,
        ...((values.numberOfBillingCycle ||
          values.paymentCycle === paymentTypeConst.ONETIME) &&
        values.totalDurationOfContract &&
        values.totalDurationOfContract !== 'Indefinite'
          ? {
              totalSession: values.totalSession,
            }
          : {}),
        timeZone: values.clientTimeZone,
        planStartDate: values?.planStartDate,
        // Conditional Scheduling
        ...(values.isConditionalSchedulingEnabled && {
          isConditionalSchedulingEnabled: values.isConditionalSchedulingEnabled,
          ...(values.schedulingConditions && {
            schedulingConditions: values.schedulingConditions,
          }),
        }),
        // Scheduling Type
        isPlatformScheduling:
          values.isPlatformScheduling === 'true' ||
          values.isPlatformScheduling === true ||
          (values.isPlatformScheduling !== false &&
            values.isPlatformScheduling !== 'false'),
        includeBioInMail: values.includeBioInMail === true,
        // Extra
        ...(values.isPaymentRequired === 'true' && {
          isReadyForPayment: values.isReadyForPayment,
        }),
      }

      Object.keys(params).forEach((key) =>
        params[key] === '' || params[key] === null || params[key] === undefined
          ? delete params[key]
          : '',
      )

      await apiCall(planId, params, clientId, values)
    } catch (e) {
      setIsUpdatePlanLoading(false)
      Error.showError(e)
    }
  }

  const apiCall = async (planId, params, clientId, formikValues) => {
    if (planId === 'create') {
      const { data } = await API.AdminAPI.coachPlan.createCoachingPlan(
        params,
        clientId,
      )
      setIsUpdatePlanLoading(false)
      Notification.showNotification(data.message, 'success')
      if (data?.sow?._id) {
        const assignedCoachId = getCoachId(data?.sow?.coachId) || params.coachId
        setCurrentCoachId(assignedCoachId)
        history.replace(`/coach/clients/${clientId}/plans/${data?.sow._id}`)
        setTimeout(() => {
          setInitialValues({
            ...formikValues,
            ...data.sow,
            sowCoachId: assignedCoachId,
          })
        }, 1000)
      }
    } else {
      const { data } = await API.AdminAPI.coachPlan.updateCoachPlan(
        params,
        clientId,
        planId,
      )
      const assignedCoachId = getCoachId(data?.sow?.coachId) || params.coachId
      setCurrentCoachId(assignedCoachId)
      setInitialValues({
        ...formikValues,
        ...data.sow,
        sowCoachId: assignedCoachId,
      })
      setIsUpdatePlanLoading(false)
      Notification.showNotification(data.message, 'success')
    }
  }

  return (
    <>
      <div className='position-relative'>
        <PageHeader
          titleText={`${clientName}`}
          {...(planId &&
            initialValues.sowStatus !== sowStatus.DRAFT && {
              descriptionFn: () => (
                <p className='font-montserrat font-medium ml-40px'>
                  {initialValues.planName}
                </p>
              ),
            })}
          hasBackArrow={true}
          colClasses='px-lg-0'
          defaultUrl={`/coach/clients/${clientId}`}
        />
        {user?.isAdmin &&
          initialValues.sowStatus !== sowStatus.TERMINATED &&
          (location?.pathname.includes('/sow') ||
            location?.pathname ===
              `/coach/clients/${clientId}/plans/${planId}`) && (
            <Button
              variant='gray'
              className='line-height-normal border-radius-3 position-absolute right-0 terminate-btn'
              disabled={isPlanTerminated || planId === 'create'}
              onClick={handleOpenModal}>
              Terminate
            </Button>
          )}
      </div>
      <Container
        className='pt-5 custom-offset-lg-1 custom-col-lg-12 px-lg-0'
        fluid>
        {planId === 'create' ? (
          // Render IndividualSOWForm for create mode
          <IndividualSOWForm
            initialValues={initialValues}
            setInitialValues={setInitialValues}
            onSubmit={onSubmit}
            viewOnlyField={planId !== 'create'}
            showModal={showModal}
            setShowModal={setShowModal}
            isPlanTerminated={isPlanTerminated}
            isUpdatePlanLoading={isUpdatePlanLoading}
            coaches={coaches}
          />
        ) : (
          // Render navigation tabs and routes for existing plans
          <>
            <div className='d-flex justify-content-between mb-5'>
              <NavTabs
                pages={pages}
                NavLink={NavLink}
                pathname={location?.pathname}
                hash={location?.hash}
              />
            </div>
            <Switch location={location}>
              <Route
                exact
                strict
                path={[
                  `/coach/clients/:clientId/plans/:planId/sow`,
                  `/coach/clients/:clientId/plans/:planId`,
                ]}>
                <IndividualSOWForm
                  initialValues={initialValues}
                  setInitialValues={setInitialValues}
                  onSubmit={onSubmit}
                  viewOnlyField={planId !== 'create'}
                  showModal={showModal}
                  setShowModal={setShowModal}
                  isPlanTerminated={isPlanTerminated}
                  isUpdatePlanLoading={isUpdatePlanLoading}
                  coaches={coaches}
                />
              </Route>
              <Route
                exact
                strict
                path={`/coach/clients/:clientId/plans/:planId/sessions`}>
                <CoachingPlanSessions sowId={planId} />
              </Route>
              <Route
                exact
                strict
                path={`/coach/clients/:clientId/plans/:planId/payment`}>
                <CoachingPlanPayment sowId={planId} />
              </Route>
            </Switch>
          </>
        )}
      </Container>
      <TerminatedModal
        showModal={showTerminatedModal}
        handleClose={handleCloseModal}
        setInitialValues={setInitialValues}
      />
    </>
  )
}

export default CoachingPlanDetails
